import { useEffect, useState } from "react";
import { Row, Col, Select, Spin } from "antd";
import { useTranslation } from "react-i18next";
import EndPoints from "../../../services/end-points";
import { IWmsDropdownItem } from "../../../models/wms/dropdown/response";
import { extractLabelFromName } from "../../../utils/helpers/translations";

export interface IStorableSelection {
  // Must match backend enum App\Enums\Wms\WmsStorableType
  // STORAGE_UNIT=1, SHELF=2, BIN=3
  storableType: 1 | 2 | 3;
  storableId: number;
}

interface WmsStorablePickerProps {
  storeId: number;
  onStorableSelected: (selection: IStorableSelection | undefined) => void;
  disabled?: boolean;
}

/**
 * Cascading Zone → Aisle → StorageUnit → Shelf? → Bin?
 * Emits { storableType, storableId } for the deepest selected level.
 * Used when linking a batch to a physical storage location.
 */
const WmsStorablePicker: React.FC<WmsStorablePickerProps> = ({
  storeId,
  onStorableSelected,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const [zones, setZones] = useState<IWmsDropdownItem[]>([]);
  const [selectedZone, setSelectedZone] = useState<number | undefined>();
  const [loadingZones, setLoadingZones] = useState(false);

  const [aisles, setAisles] = useState<IWmsDropdownItem[]>([]);
  const [selectedAisle, setSelectedAisle] = useState<number | undefined>();
  const [loadingAisles, setLoadingAisles] = useState(false);

  const [storageUnits, setStorageUnits] = useState<IWmsDropdownItem[]>([]);
  const [selectedStorageUnit, setSelectedStorageUnit] = useState<number | undefined>();
  const [loadingStorageUnits, setLoadingStorageUnits] = useState(false);

  const [shelves, setShelves] = useState<IWmsDropdownItem[]>([]);
  const [selectedShelf, setSelectedShelf] = useState<number | undefined>();
  const [selectedShelfHasBins, setSelectedShelfHasBins] = useState(false);
  const [loadingShelves, setLoadingShelves] = useState(false);

  const [bins, setBins] = useState<IWmsDropdownItem[]>([]);
  const [selectedBin, setSelectedBin] = useState<number | undefined>();
  const [loadingBins, setLoadingBins] = useState(false);

  // ── Clear helpers ─────────────────────────────────────────────────────────
  const clearFromAisle = () => {
    setSelectedAisle(undefined);
    setStorageUnits([]);
    clearFromStorageUnit();
  };
  const clearFromStorageUnit = () => {
    setSelectedStorageUnit(undefined);
    setShelves([]);
    clearFromShelf();
  };
  const clearFromShelf = () => {
    setSelectedShelf(undefined);
    setSelectedShelfHasBins(false);
    setBins([]);
    setSelectedBin(undefined);
  };

  // ── Load zones when storeId changes ──────────────────────────────────────
  useEffect(() => {
    setZones([]);
    setSelectedZone(undefined);
    clearFromAisle();
    if (!storeId) return;
    setLoadingZones(true);
    EndPoints.wmsShared
      .getDropdownZones({ storeId })
      .then(({ data }) =>
        setZones(
          data.data.map((z: any) => ({
            id: z.id,
            label: extractLabelFromName(z.name, `Zone ${z.id}`),
          }))
        )
      )
      .finally(() => setLoadingZones(false));
  }, [storeId]);

  // ── Load aisles ───────────────────────────────────────────────────────────
  useEffect(() => {
    setAisles([]);
    clearFromAisle();
    if (!selectedZone) return;
    setLoadingAisles(true);
    EndPoints.wmsShared
      .getDropdownAisles({ zoneId: selectedZone })
      .then(({ data }) =>
        setAisles(
          data.data.map((a: any) => ({
            id: a.id,
            label: extractLabelFromName(a.name, `Aisle ${a.id}`),
          }))
        )
      )
      .finally(() => setLoadingAisles(false));
  }, [selectedZone]);

  // ── Load storage units ────────────────────────────────────────────────────
  useEffect(() => {
    setStorageUnits([]);
    clearFromStorageUnit();
    if (!selectedAisle) return;
    setLoadingStorageUnits(true);
    EndPoints.wmsShared
      .getDropdownStorageUnits({ aisleId: selectedAisle })
      .then(({ data }) =>
        setStorageUnits(
          data.data.map((su: any) => ({
            id: su.id,
            label: extractLabelFromName(su.name, `Storage Unit ${su.id}`),
            hasShelves: su.hasShelves,
          }))
        )
      )
      .finally(() => setLoadingStorageUnits(false));
  }, [selectedAisle]);

  // ── Load shelves ──────────────────────────────────────────────────────────
  useEffect(() => {
    setShelves([]);
    clearFromShelf();
    if (!selectedStorageUnit) return;
    setLoadingShelves(true);
    EndPoints.wmsShared
      .getDropdownShelves({ storageUnitId: selectedStorageUnit })
      .then(({ data }) =>
        setShelves(
          data.data.map((s: any) => ({
            id: s.id,
            label: extractLabelFromName(s.name, `Shelf ${s.id}`),
            hasBins: s.hasBins,
          }))
        )
      )
      .finally(() => setLoadingShelves(false));
  }, [selectedStorageUnit]);

  // ── Load bins ─────────────────────────────────────────────────────────────
  useEffect(() => {
    setBins([]);
    setSelectedBin(undefined);
    if (!selectedShelf || !selectedShelfHasBins) return;
    setLoadingBins(true);
    EndPoints.wmsShared
      .getDropdownBins({ shelfId: selectedShelf })
      .then(({ data }) =>
        setBins(
          data.data.map((b: any) => ({
            id: b.id,
            label: extractLabelFromName(b.name, `Bin ${b.id}`),
          }))
        )
      )
      .finally(() => setLoadingBins(false));
  }, [selectedShelf, selectedShelfHasBins]);

  // ── Emit storable selection whenever deepest level changes ────────────────
  // Skip during loading transitions to avoid briefly emitting undefined
  // while shelves/bins are being fetched for the selected parent.
  useEffect(() => {
    if (loadingShelves || loadingBins) return;

    if (selectedShelfHasBins && selectedBin) {
      onStorableSelected({ storableType: 3, storableId: selectedBin });
    } else if (selectedShelf) {
      onStorableSelected({ storableType: 2, storableId: selectedShelf });
    } else if (selectedStorageUnit) {
      // Even if the storage unit has shelves, allow using the storage unit itself
      // as the storable when the user hasn't selected a shelf/bin.
      onStorableSelected({ storableType: 1, storableId: selectedStorageUnit });
    } else {
      onStorableSelected(undefined);
    }
  }, [selectedBin, selectedShelf, selectedStorageUnit, shelves, loadingShelves, loadingBins, selectedShelfHasBins]);

  // ── Render helper ─────────────────────────────────────────────────────────
  const renderSelect = (
    label: string,
    items: IWmsDropdownItem[],
    value: number | undefined,
    onChange: (val: number | undefined) => void,
    loading: boolean
  ) => (
    <Col xs={24} sm={12} md={8}>
      <div style={{ marginBottom: 4, fontWeight: 500, fontSize: 13 }}>{label}</div>
      <Select
        style={{ width: "100%" }}
        value={value}
        onChange={onChange}
        allowClear
        showSearch
        optionFilterProp="children"
        placeholder={label}
        loading={loading}
        disabled={disabled || (items.length === 0 && !loading)}
        notFoundContent={loading ? <Spin size="small" /> : null}
      >
        {items.map((item) => (
          <Select.Option key={item.id} value={item.id}>
            {item.label} {item.code ? `(${item.code})` : ""}
          </Select.Option>
        ))}
      </Select>
    </Col>
  );

  return (
    <Row gutter={[12, 12]}>
      {renderSelect(t("select_zone"), zones, selectedZone, (val) => setSelectedZone(val), loadingZones)}

      {selectedZone &&
        renderSelect(t("select_aisle"), aisles, selectedAisle, (val) => setSelectedAisle(val), loadingAisles)}

      {selectedAisle &&
        renderSelect(
          t("select_storage_unit"),
          storageUnits,
          selectedStorageUnit,
          (val) => setSelectedStorageUnit(val),
          loadingStorageUnits
        )}

      {selectedStorageUnit && shelves.length > 0 &&
        renderSelect(
          t("select_shelf"),
          shelves,
          selectedShelf,
          (val) => {
            setSelectedShelf(val);
            const item = shelves.find((s) => s.id === val);
            setSelectedShelfHasBins(item?.hasBins ?? false);
          },
          loadingShelves
        )}

      {selectedShelf && selectedShelfHasBins &&
        renderSelect(t("select_bin"), bins, selectedBin, (val) => setSelectedBin(val), loadingBins)}
    </Row>
  );
};

export default WmsStorablePicker;
